import { useState, useRef, useEffect, useCallback } from "react";
import { analyzeDocument } from "../utils/api";

// ─── Upload helpers ────────────────────────────────────────────────────────
function getBase() {
  return (window.site && window.site.submissionApiBaseUrl) ||
    (window.site && window.site.config && (window.site.config.submissionApi || window.site.config.screeningApi)) || "";
}
function getAppname() { return (window.app && window.app.appName) || ""; }
function getEnv()     { return (window.app && window.app.appEnvName) || "dev"; }
function getToken()   {
  const s = window.app && window.app.storage;
  return (s && s.getItem && s.getItem("token")) || localStorage.getItem("token") ||
    "0381db972d7447216018b15aa1b54dc3e51cd3e282b78b3424fa0726828e766c995495fcc2d8c71d86dc7777ef43ca92ff51f8e7027ba56b9aa0b6a47f76d16ab35076d6c1ffd8fa85f3b1cbf4a04a05ccebb5cb80a59f8687d5c0fb4cf5b34cea18c7bea57931c3b34cc3209df2b956e544cf2b2b0cd5601a065ec74ee911db6467905bb959c330fcc01beb2c6a826d25e0e2736dec31a1c25ccd82e65eefa3f5a34a9810151e7be83bd9f533a16e9d8a9124936329e9db585d4aa32b8f052e8fe649f7e11e35afc8d248679af62612ac3ed7ba4b0b114a8b19c645909935ca32260ad829cc54f1b701575cd7c0e7074bb0";
}

async function getSignedUrl(sourcePath, bucket) {
  const siteConf = (window.site && window.site.config) || {};
  const source_bucket = bucket || siteConf.assetSourceBucket || "";
  const base   = (window.site && window.site.submissionApiBaseUrl) || "";
  const appname = window.app && window.app.appName;
  const env     = window.app && window.app.appEnvName;
  const token   = (window.app && window.app.storage && window.app.storage.getItem("token")) ||
                  localStorage.getItem("token") || getToken();
  const resp = await fetch(`${base}/uploadURL?appname=${appname}&env=${env}&token=${token}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source_bucket, sourcepath: sourcePath }),
  });
  const data = await resp.json();
  if (!data || !data.result) throw new Error("Signed URL missing");
  return data.result;
}

async function putToS3(signedUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (onProgress) xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener("load", () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload failed"))
    );
    xhr.addEventListener("error", () => reject(new Error("Upload failed")));
    xhr.open("PUT", signedUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.send(file);
  });
}

function getGraphicsCdn()    { return ((window.site && window.site.config) || {}).sourceCloudFront   || "https://envoi-common-resources.imgix.net"; }
function getGraphicsBucket() { return ((window.site && window.site.config) || {}).assetSourceBucket  || ""; }

// ─── Steps ─────────────────────────────────────────────────────────────────
const STEPS = [
  { key: "personal",  label: "Personal",  title: "Personal Information", sub: "Verify and complete your personal details extracted from your document." },
  { key: "company",   label: "Company",   title: "Company & Social",     sub: "Add your company info, profile images, and social media links." },
  { key: "addresses", label: "Addresses", title: "Billing & Shipping",   sub: "Enter your billing and shipping addresses." },
  { key: "payment",   label: "Payment",   title: "Payment Information",  sub: "Add your banking details and payment gateway credentials." },
];

// ─── Step indicator ────────────────────────────────────────────────────────
function StepIndicator({ step }) {
  return (
    <div className="sub-steps">
      {STEPS.map(({ label }, i) => (
        <div key={i} className="sub-step-item">
          {i > 0 && <div className={`sub-step-conn${i <= step ? " filled" : ""}`} />}
          <div className={`sub-step${i < step ? " done" : i === step ? " active" : ""}`}>
            <div className="sub-step-num">{i < step ? "✓" : i + 1}</div>
            <span className="sub-step-lbl">{label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Gateway section ────────────────────────────────────────────────────────
function GatewaySection({ title, state, setState, fields }) {
  return (
    <div className="gateway-section">
      <p className="form-section-label" style={{ marginBottom: "8px" }}>{title}</p>
      <div className="form-grid three-col">
        {fields.map(({ key, label, placeholder }) => (
          <div className="field-group" key={key}>
            <label>{label}</label>
            <input type="text" value={state[key] || ""}
              onChange={e => setState(s => ({ ...s, [key]: e.target.value }))}
              placeholder={placeholder} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export default function StepScanAndVerify({ session, updateSession, onNext, onBack }) {

  // ── Scan ──────────────────────────────────────────────────────────────────
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [scanning, setScanning]   = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [docS3Key, setDocS3Key]   = useState(null);
  const [extracted, setExtracted] = useState(null);

  const [docFields, setDocFields] = useState({
    documentNumber: "", issueDate: "", idType: "", expirationDate: "",
    placeOfBirth: "", mrzCode: "", dateOfBirth: "",
  });

  // ── Wizard ────────────────────────────────────────────────────────────────
  const [step, setStep]   = useState(0);
  const [error, setError] = useState("");

  // ── Lookups ───────────────────────────────────────────────────────────────
  const [countries, setCountries]               = useState([]);
  const [regions, setRegions]                   = useState([]);
  const [companyRegions, setCompanyRegions]     = useState([]);
  const [billingRegions, setBillingRegions]     = useState([]);
  const [shippingRegions, setShippingRegions]   = useState([]);
  const [bankCountryRegions, setBankCountryRegions] = useState([]);
  const [creatorTypes, setCreatorTypes]         = useState([]);
  const [jobTypes, setJobTypes]                 = useState([]);
  const [genderOptions, setGenderOptions]       = useState([]);
  const [ethnicityOptions, setEthnicityOptions] = useState([]);

  // ── Personal ──────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: session.email || "",
    address: "", city: "", country: "", state: "", postalCode: "",
    homePhoneDialingCode: "", homePhone: "",
    mobileDialingCode: "", mobileNumber: "",
    dateOfBirth: "", ethnicity: "", gender: "", creatorType: "", jobType: "",
    description: "",
  });

  // ── Company ───────────────────────────────────────────────────────────────
  const [company, setCompany] = useState({
    name: "", suite: "", dunsNumber: "", federalTaxPayerId: "",
    address: "", city: "", state: "", country: "", postalCode: "", email: "",
    officePhoneDialingCode: "", officePhone: "",
    mobileDialingCode: "", mobileNumber: "",
  });

  // ── Graphics ──────────────────────────────────────────────────────────────
  const [graphics, setGraphics]         = useState({ landscapeUrl: "", portraitUrl: "", logoUrl: "" });
  const [imgProgress, setImgProgress]   = useState({});

  // ── Social ────────────────────────────────────────────────────────────────
  const [social, setSocial] = useState({
    instagram: "", facebook: "", twitter: "", youtube: "",
    soundcloud: "", spotify: "", appleItunes: "", website: "",
    imdb: "", filmfreeway: "", stage32: "", linkedin: "",
  });

  // ── Addresses ─────────────────────────────────────────────────────────────
  const [addresses, setAddresses] = useState({
    billingLocationName: "", billingAddress: "", billingSuite: "",
    billingCity: "", billingState: "", billingCountry: "", billingPostalCode: "",
    billingDeliveryNotes: "",
    sameAsBilling: false,
    shippingLocationName: "", shippingAddress: "", shippingSuite: "",
    shippingCity: "", shippingState: "", shippingCountry: "", shippingPostalCode: "",
    shippingDeliveryNotes: "",
  });

  // ── Payment ───────────────────────────────────────────────────────────────
  const [paymentInfo, setPaymentInfo] = useState({
    bankName: "", bankAddress: "", bankCity: "", bankState: "", bankCountry: "", bankPostalCode: "",
    bankAccountNumber: "", bankRoutingNumber: "",
    swiftCode: "", iban: "",
  });

  // ── Payment gateways ──────────────────────────────────────────────────────
  const [squareGateway, setSquareGateway] = useState({
    account_id: "", bearer_token: "", refresh_token: "", client_id: "", client_secret: "", app_id: "",
  });
  const [paypalGateway, setPaypalGateway] = useState({
    gateway: "paypal", environment: "sandbox", account_id: "", client_id: "", client_secret: "",
  });
  const [braintreeGateway, setBraintreeGateway] = useState({
    gateway: "braintree", environment: "sandbox", account_id: "", api_key: "", client_id: "",
  });
  const [quickbooksGateway, setQuickbooksGateway] = useState({
    gateway: "quickbooks", account_id: "", bearer_token: "", refresh_token: "", client_id: "", client_secret: "",
  });

  const fileRef = useRef();

  // ── Load countries ────────────────────────────────────────────────────────
  useEffect(() => {
    const cfg = (window.site && window.site.config) || {};
    const commonApi = cfg.commonApi || cfg.commonResourcesUrl || "";
    const appname = getAppname() || "envoidemo";
    if (!commonApi) return;
    fetch(`${commonApi}/getcountries?appname=${appname}`, { method: "POST" })
      .then(r => r.json())
      .then(data => { if (data?.result) setCountries(data.result); })
      .catch(() => {});
  }, []);

  // ── Load creator/job types ────────────────────────────────────────────────
  useEffect(() => {
    const cfg = (window.site && window.site.config) || {};
    const commonApi = cfg.commonApi || cfg.commonResourcesUrl || "";
    const appname = getAppname();
    if (!commonApi || !appname) return;
    fetch(`${commonApi}/lookups/creatortype,team,school/${appname}`)
      .then(r => r.json())
      .then(data => {
        const result = data?.result;
        if (Array.isArray(result)) {
          const cd = result.find(r => r.type === "creatortype");
          const td = result.find(r => r.type === "team");
          const sd = result.find(r => r.type === "school");
          const allJobs = [...(cd?.jobType || []), ...(td?.jobType || []), ...(sd?.jobType || [])];
          setCreatorTypes(cd?.values || []);
          setJobTypes([...new Set(allJobs)]);
        }
      })
      .catch(() => {});
  }, []);

  // ── Load gender / ethnicity ───────────────────────────────────────────────
  useEffect(() => {
    const cfg = (window.site && window.site.config) || {};
    const cacheUrl = cfg.apiCacheUrl || "https://envoi-api-cache.dev2.envoi.cloud";
    const appname = getAppname();
    if (!appname) return;
    fetch(`${cacheUrl}/configs/${appname}/dev`)
      .then(r => r.json())
      .then(data => {
        const c = data?.result?.config;
        if (c) {
          if (Array.isArray(c.gender)) setGenderOptions(c.gender);
          if (Array.isArray(c.ethnicity)) setEthnicityOptions(c.ethnicity);
        }
      })
      .catch(() => {});
  }, []);

  const fetchRegions = useCallback((alpha3, setter) => {
    const cfg = (window.site && window.site.config) || {};
    const commonApi = cfg.commonApi || cfg.commonResourcesUrl || "";
    if (!commonApi || !alpha3) { setter([]); return; }
    fetch(`${commonApi}/getregions?alpha3=${alpha3}`, { method: "POST" })
      .then(r => r.json())
      .then(data => setter(data?.result || []))
      .catch(() => setter([]));
  }, []);

  // Resolve extracted country name → alpha3 after scan
  useEffect(() => {
    if (!extracted || !countries.length || !form.country) return;
    if (countries.find(c => c.alpha3 === form.country)) {
      fetchRegions(form.country, setRegions);
      return;
    }
    const match = countries.find(c => c.countryname?.toLowerCase() === form.country?.toLowerCase());
    if (match) {
      setForm(f => ({ ...f, country: match.alpha3 }));
      fetchRegions(match.alpha3, setRegions);
    }
  }, [extracted, countries, fetchRegions]); // eslint-disable-line

  const handleAddressChange = (field, value) => {
    setAddresses(prev => {
      const next = { ...prev, [field]: value };
      if (field === "sameAsBilling" && value) {
        next.shippingLocationName  = prev.billingLocationName;
        next.shippingAddress       = prev.billingAddress;
        next.shippingSuite         = prev.billingSuite;
        next.shippingCity          = prev.billingCity;
        next.shippingState         = prev.billingState;
        next.shippingCountry       = prev.billingCountry;
        next.shippingPostalCode    = prev.billingPostalCode;
        next.shippingDeliveryNotes = prev.billingDeliveryNotes;
        setShippingRegions([...billingRegions]);
      }
      return next;
    });
  };

  // ── Document file handling ─────────────────────────────────────────────────
  const handleFile = (f) => {
    if (!f) return;
    setFile(f); setExtracted(null); setDocS3Key(null); setError("");
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleScan = async () => {
    if (!file) return setError("Please upload a photo of your document first.");
    setScanning(true); setError(""); setUploadPct(0);
    try {
      const appname = getAppname();
      const cleanName = file.name.replace(/\s+/g, "");
      const ext = cleanName.split(".").pop();
      const base = cleanName.replace(`.${ext}`, "");
      const sourcePath = `${appname}/faceverify/${session.userId || "guest"}/${base}${Date.now()}.${ext}`;

      setUploadPct(10);
      const signedUrl = await getSignedUrl(sourcePath);
      setUploadPct(20);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", (ev) => {
          if (ev.lengthComputable) setUploadPct(Math.round(20 + (ev.loaded / ev.total) * 60));
        });
        xhr.addEventListener("load", () =>
          xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload failed"))
        );
        xhr.addEventListener("error", () => reject(new Error("Upload failed")));
        xhr.open("PUT", signedUrl);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.send(file);
      });
      setDocS3Key(sourcePath);
      setUploadPct(80);

      const resultAPI = await analyzeDocument(sourcePath, session.docType);
      setUploadPct(100);
      const fields = resultAPI?.result?.extracted || {};
      setExtracted(fields);
      setStep(0);

      setForm(f => ({
        ...f,
        firstName:   fields.firstName   || f.firstName,
        lastName:    fields.lastName    || f.lastName,
        address:     fields.address     || f.address,
        city:        fields.city        || f.city,
        state:       fields.state       || f.state,
        postalCode:  fields.zipCode     || fields.postalCode || f.postalCode,
        country:     fields.country     || f.country,
        dateOfBirth: fields.dateOfBirth || f.dateOfBirth,
      }));

      setDocFields({
        documentNumber: fields.documentNumber || "",
        issueDate:      fields.issueDate      || "",
        idType:         fields.idType         || "",
        expirationDate: fields.expirationDate || "",
        placeOfBirth:   fields.placeOfBirth   || "",
        mrzCode:        fields.mrzCode        || "",
        dateOfBirth:    fields.dateOfBirth    || "",
      });
    } catch (e) { setError(e.message); }
    finally { setScanning(false); setUploadPct(0); }
  };

  // ── Graphics upload ────────────────────────────────────────────────────────
  const handleGraphicsUpload = async (imgFile, fieldKey) => {
    if (!imgFile || !imgFile.type.startsWith("image/")) return;
    const appname = getAppname();
    const userId  = session.userId || session.email || "guest";
    const ext     = imgFile.name.split(".").pop();
    const sourcePath = `${appname}/submission/creator/${userId}/${fieldKey}_${Date.now()}.${ext}`;
    const bucket = getGraphicsBucket();

    setImgProgress(p => ({ ...p, [fieldKey]: 0 }));
    try {
      const signedUrl = await getSignedUrl(sourcePath, bucket);
      await putToS3(signedUrl, imgFile, (pct) =>
        setImgProgress(p => ({ ...p, [fieldKey]: pct }))
      );
      const cdn = getGraphicsCdn();
      setGraphics(g => ({ ...g, [`${fieldKey}Url`]: `${cdn}/${sourcePath}` }));
    } catch (e) {
      setError(`Image upload failed: ${e.message}`);
    } finally {
      setImgProgress(p => ({ ...p, [fieldKey]: null }));
    }
  };

  const dialCodes = countries.map(c => {
    const idc = c.idc || (c.phone_code ? `+${c.phone_code}` : "");
    return { value: idc, label: `${c.countryname}${idc ? ` (${idc})` : ""}` };
  });

  // ── Navigation ────────────────────────────────────────────────────────────
  const handleNext = () => {
    if (step === 0) {
      if (!form.firstName.trim()) return setError("First Name is required.");
      if (!form.lastName.trim())  return setError("Last Name is required.");
    }
    setError("");
    setStep(s => s + 1);
  };

  const handleBack = () => {
    setError("");
    if (step === 0) setExtracted(null);
    else setStep(s => s - 1);
  };

  // ── Save all collected data to session then proceed to face verify step ────
  const handleSaveAndContinue = () => {
    if (!form.firstName.trim()) { setStep(0); return setError("First Name is required."); }
    if (!form.lastName.trim())  { setStep(0); return setError("Last Name is required."); }

    const appname = getAppname();
    const getCountryName = (alpha3) => (countries.find(x => x.alpha3 === alpha3)?.countryname || alpha3);

    updateSession({
      // Personal
      status:      "active",
      firstname:   form.firstName.trim(),
      lastname:    form.lastName.trim(),
      emailid:     (form.email || session.email).trim(),
      usertype:    session.role || "creator",
      appname,
      creatorType: form.creatorType,
      jobType:     form.jobType,
      dateOfBirth: form.dateOfBirth,
      ethnicity:   form.ethnicity,
      gender:      form.gender,
      description: form.description,
      companyAddress: {
        name:              company.name,
        suite:             company.suite,
        address:           company.address || form.address,
        city:              company.city    || form.city,
        country:           company.country || form.country,
        countryName:       getCountryName(company.country || form.country),
        state:             company.state   || form.state,
        postalCode:        company.postalCode || form.postalCode,
        email:             company.email,
        dunsNumber:        company.dunsNumber,
        federalTaxPayerId: company.federalTaxPayerId,
      },
      ...(form.homePhone      ? { homePhone:    { dialingCode: form.homePhoneDialingCode,      number: form.homePhone } }    : {}),
      ...(form.mobileNumber   ? { mobileNumber: { dialingCode: form.mobileDialingCode,         number: form.mobileNumber } } : {}),
      ...(company.officePhone ? { officePhone:  { dialingCode: company.officePhoneDialingCode, number: company.officePhone } } : {}),
      addresses: {
        billing: {
          locationName:  addresses.billingLocationName,
          address:       addresses.billingAddress,
          suite:         addresses.billingSuite,
          city:          addresses.billingCity,
          state:         addresses.billingState,
          country:       addresses.billingCountry,
          countryName:   getCountryName(addresses.billingCountry),
          postalCode:    addresses.billingPostalCode,
          deliveryNotes: addresses.billingDeliveryNotes,
        },
        shipping: addresses.sameAsBilling ? null : {
          locationName:  addresses.shippingLocationName,
          address:       addresses.shippingAddress,
          suite:         addresses.shippingSuite,
          city:          addresses.shippingCity,
          state:         addresses.shippingState,
          country:       addresses.shippingCountry,
          countryName:   getCountryName(addresses.shippingCountry),
          postalCode:    addresses.shippingPostalCode,
          deliveryNotes: addresses.shippingDeliveryNotes,
        },
        sameAsBilling: addresses.sameAsBilling,
      },
      paymentInfo: {
        ...paymentInfo,
        bankCountryName: getCountryName(paymentInfo.bankCountry),
      },
      paymentGateways: [squareGateway, paypalGateway, braintreeGateway, quickbooksGateway],
      landscape_logo_image: graphics.landscapeUrl || "",
      portrait_logo_image:  graphics.portraitUrl  || "",
      logo:                 graphics.logoUrl       || "",
      socialLinks: social,
      idS3Key:   docS3Key,
      extracted: docFields,
    });

    onNext();
  };

  // ═══════════════════════════════════════════════════════════════
  // SCAN PHASE
  // ═══════════════════════════════════════════════════════════════
  const renderScanPhase = () => (
    <>
      <div
        className={`upload-zone ${file ? "has-file" : ""}`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileRef.current?.click()}
      >
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
          style={{ display: "none" }} onChange={(e) => handleFile(e.target.files[0])} />
        {preview ? (
          <div className="upload-preview">
            <img src={preview} alt="ID preview" />
            <button className="replace-btn"
              onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}>Replace</button>
          </div>
        ) : (
          <div className="upload-prompt">
            <span className="upload-icon">⬆</span>
            <span>Drag &amp; drop or click to upload</span>
            <span className="upload-hint">JPEG, PNG, PDF · Max 10 MB</span>
          </div>
        )}
      </div>

      {scanning && uploadPct > 0 && (
        <div style={{ marginTop: "10px", height: "4px", background: "rgba(255,255,255,0.1)", borderRadius: "2px", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${uploadPct}%`, background: "var(--highlight,#e9c548)", borderRadius: "2px", transition: "width 0.3s ease" }} />
        </div>
      )}

      <div className="action-row" style={{ marginTop: "14px" }}>
        <button className="btn-ghost" onClick={onBack}>← Back</button>
        <button className="btn-primary" onClick={handleScan} disabled={!file || scanning}>
          {scanning
            ? <><span className="spinner" /> {uploadPct < 80 ? `Uploading… ${uploadPct}%` : "Analyzing…"}</>
            : "Scan document →"}
        </button>
      </div>
    </>
  );

  // ═══════════════════════════════════════════════════════════════
  // STEP 0 — PERSONAL
  // ═══════════════════════════════════════════════════════════════
  const renderPersonal = () => {
    const inp = (label, key, type = "text", placeholder = "", required = false) => (
      <div className="field-group" key={key}>
        <label>{label}{required && <span style={{ color: "#ff5555", marginLeft: 3 }}>*</span>}</label>
        <input type={type} value={form[key]}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          placeholder={placeholder} />
      </div>
    );

    const sel = (label, key, opts) => (
      <div className="field-group" key={key}>
        <label>{label}</label>
        <select value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}>
          <option value="">Select {label}</option>
          {opts.map((o, i) => (
            <option key={i} value={typeof o === "string" ? o : o.value}>
              {typeof o === "string" ? o : o.label}
            </option>
          ))}
        </select>
      </div>
    );

    const phoneRow = (label, codeKey, numKey, placeholder) => (
      <div className="field-group" key={numKey}>
        <label>{label}</label>
        <div style={{ display: "flex", gap: "8px" }}>
          <select value={form[codeKey]}
            onChange={e => setForm(f => ({ ...f, [codeKey]: e.target.value }))}
            style={{ width: "130px", flexShrink: 0 }}>
            <option value="">Code</option>
            {dialCodes.map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
          </select>
          <input type="tel" value={form[numKey]}
            onChange={e => setForm(f => ({ ...f, [numKey]: e.target.value }))}
            placeholder={placeholder} style={{ flex: 1 }} />
        </div>
      </div>
    );

    return (
      <>
        {extracted && (
          <div className="extracted-header">
            <span className="check-icon">✓</span>
            <span>Information extracted — please verify or correct below</span>
          </div>
        )}

        <p className="form-section-label">Basic Info</p>
        <div className="form-grid three-col">
          {inp("First Name", "firstName", "text",  "Enter first name", true)}
          {inp("Last Name",  "lastName",  "text",  "Enter last name",  true)}
          {inp("Email",      "email",     "email", "Enter email",      true)}
        </div>
        <div className="form-grid three-col">
          {inp("Address",     "address",    "text", "Street address")}
          {inp("City",        "city",       "text", "City")}
          {inp("Postal Code", "postalCode", "text", "Postal code")}
        </div>
        <div className="form-grid two-col">
          <div className="field-group">
            <label>Country</label>
            <select value={form.country} onChange={e => {
              setForm(f => ({ ...f, country: e.target.value, state: "" }));
              fetchRegions(e.target.value, setRegions);
            }}>
              <option value="">Select Country</option>
              {countries.map(c => <option key={c.alpha3} value={c.alpha3}>{c.countryname}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label>State / Region</label>
            <select value={form.state}
              onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
              disabled={!form.country || !regions.length}>
              <option value="">Select State / Region</option>
              {regions.map((r, i) => <option key={i} value={r.regionname}>{r.regionname}</option>)}
            </select>
          </div>
        </div>

        <p className="form-section-label">Contact</p>
        <div className="form-grid three-col">
          {phoneRow("Home Phone",    "homePhoneDialingCode", "homePhone",    "Home phone")}
          {phoneRow("Mobile Number", "mobileDialingCode",    "mobileNumber", "Mobile number")}
          {inp("Date of Birth", "dateOfBirth", "date")}
        </div>

        <p className="form-section-label">Profile</p>
        <div className="form-grid three-col">
          {sel("Ethnicity", "ethnicity", ethnicityOptions)}
          {sel("Gender",    "gender",    genderOptions)}
          {sel(session.role === "partner" ? "Partner Type" : "Creator Type", "creatorType", creatorTypes)}
        </div>
        <div className="form-grid two-col">
          {sel("Job Type", "jobType", jobTypes)}
        </div>
        <div className="form-grid one-col">
          <div className="field-group">
            <label>Description</label>
            <textarea value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Enter description..."
              style={{ minHeight: "80px", resize: "vertical" }}
            />
          </div>
        </div>

        <hr className="form-divider" />
        <p className="form-section-label">Document Details</p>
        <div className="form-grid three-col">
          {["documentNumber","issueDate","expirationDate","idType","placeOfBirth","mrzCode"].map(key => (
            <div className="field-group" key={key}>
              <label>{key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}</label>
              <input type="text" value={docFields[key]}
                onChange={e => setDocFields(d => ({ ...d, [key]: e.target.value }))}
                placeholder={key.replace(/([A-Z])/g, " $1")} />
            </div>
          ))}
        </div>
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // STEP 1 — COMPANY + GRAPHICS + SOCIAL
  // ═══════════════════════════════════════════════════════════════
  const renderCompany = () => {
    const ci = (label, key, type = "text", placeholder = "") => (
      <div className="field-group" key={key}>
        <label>{label}</label>
        <input type={type} value={company[key]}
          onChange={e => setCompany(c => ({ ...c, [key]: e.target.value }))}
          placeholder={placeholder} />
      </div>
    );

    const phoneRow = (label, codeKey, numKey, placeholder) => (
      <div className="field-group" key={numKey}>
        <label>{label}</label>
        <div style={{ display: "flex", gap: "8px" }}>
          <select value={company[codeKey]}
            onChange={e => setCompany(c => ({ ...c, [codeKey]: e.target.value }))}
            style={{ width: "130px", flexShrink: 0 }}>
            <option value="">Code</option>
            {dialCodes.map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
          </select>
          <input type="tel" value={company[numKey]}
            onChange={e => setCompany(c => ({ ...c, [numKey]: e.target.value }))}
            placeholder={placeholder} style={{ flex: 1 }} />
        </div>
      </div>
    );

    // Each item has the display aspect class and humanized size hint
    const graphicItems = [
      { key: "landscape", label: "Landscape Image", hint: "1920 × 1080",  cls: "landscape", url: graphics.landscapeUrl },
      { key: "portrait",  label: "Portrait Image",  hint: "1000 × 1294",  cls: "portrait",  url: graphics.portraitUrl  },
      { key: "logo",      label: "Logo",            hint: "Square PNG",   cls: "logo",      url: graphics.logoUrl      },
    ];

    const socialPlatforms = [
      { key: "instagram",  label: "Instagram"    },
      { key: "facebook",   label: "Facebook"     },
      { key: "twitter",    label: "Twitter / X"  },
      { key: "youtube",    label: "YouTube"      },
      { key: "soundcloud", label: "SoundCloud"   },
      { key: "spotify",    label: "Spotify"      },
      { key: "appleItunes",label: "Apple iTunes" },
      { key: "website",    label: "Website"      },
      { key: "imdb",       label: "IMDB"         },
      { key: "filmfreeway",label: "Film Freeway" },
      { key: "stage32",    label: "Stage 32"     },
      { key: "linkedin",   label: "LinkedIn"     },
    ];

    return (
      <>
        <p className="form-section-label">Company Info</p>
        <div className="form-grid three-col">
          {ci("Company Name",         "name",             "text",  "Company name")}
          {ci("Suite / Unit",         "suite",            "text",  "Suite or unit #")}
          {ci("Email",                "email",            "email", "Company email")}
        </div>
        <div className="form-grid two-col">
          {ci("DUNS Number",          "dunsNumber",       "text",  "DUNS Number")}
          {ci("Federal Tax Payer ID", "federalTaxPayerId","text",  "Federal Tax Payer ID")}
        </div>

        <p className="form-section-label">Company Address</p>
        <div className="form-grid three-col">
          {ci("Address",     "address",    "text", "Street address")}
          {ci("City",        "city",       "text", "City")}
          {ci("Postal Code", "postalCode", "text", "Postal code")}
        </div>
        <div className="form-grid two-col">
          <div className="field-group">
            <label>Country</label>
            <select value={company.country} onChange={e => {
              setCompany(c => ({ ...c, country: e.target.value, state: "" }));
              fetchRegions(e.target.value, setCompanyRegions);
            }}>
              <option value="">Select Country</option>
              {countries.map(c => <option key={c.alpha3} value={c.alpha3}>{c.countryname}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label>State / Region</label>
            <select value={company.state}
              onChange={e => setCompany(c => ({ ...c, state: e.target.value }))}
              disabled={!company.country || !companyRegions.length}>
              <option value="">Select State / Region</option>
              {companyRegions.map((r, i) => <option key={i} value={r.regionname}>{r.regionname}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grid two-col">
          {phoneRow("Office Phone",  "officePhoneDialingCode", "officePhone",  "Office phone")}
          {phoneRow("Mobile Number", "mobileDialingCode",      "mobileNumber", "Mobile number")}
        </div>

        <hr className="form-divider" />
        <p className="form-section-label">Graphics</p>
        <div className="form-grid three-col">
          {graphicItems.map(item => (
            <div className="field-group" key={item.key}>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {item.label}
                <span className="field-hint" style={{ display: "inline", fontSize: 10, marginLeft: 4 }}>{item.hint}</span>
              </label>

              {/* Progress bar */}
              {imgProgress[item.key] != null && (
                <div style={{ height: "3px", background: "rgba(255,255,255,0.1)", borderRadius: "2px", overflow: "hidden", marginBottom: "5px" }}>
                  <div style={{ height: "100%", width: `${imgProgress[item.key]}%`, background: "var(--highlight,#e9c548)", transition: "width 0.3s" }} />
                </div>
              )}

              {/* Upload box — image fills box with correct aspect ratio via CSS */}
              <label className={`img-upload-wrap ${item.cls}${item.url ? " has-file" : ""}`}>
                {item.url
                  ? <>
                      <img src={item.url} alt={item.label} />
                      <div className="img-upload-overlay">🔄 Replace</div>
                    </>
                  : <div className="img-upload-placeholder">
                      <span>⬆</span>
                      <span>Click to upload</span>
                    </div>
                }
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => {
                    if (e.target.files[0]) handleGraphicsUpload(e.target.files[0], item.key);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
          ))}
        </div>

        <hr className="form-divider" />
        <p className="form-section-label">Social &amp; Web</p>
        <div className="form-grid three-col">
          {socialPlatforms.map(p => (
            <div className="field-group" key={p.key}>
              <label>{p.label}</label>
              <input type="url" value={social[p.key]}
                onChange={e => setSocial(s => ({ ...s, [p.key]: e.target.value }))}
                placeholder="https://..." />
            </div>
          ))}
        </div>
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // STEP 2 — ADDRESSES
  // ═══════════════════════════════════════════════════════════════
  const renderAddresses = () => {
    const addrBlock = (prefix, regionList, regionSetter, countryKey, stateKey) => (
      <>
        <div className="form-grid three-col">
          <div className="field-group">
            <label>Location Name</label>
            <input type="text" value={addresses[`${prefix}LocationName`]}
              onChange={e => handleAddressChange(`${prefix}LocationName`, e.target.value)}
              placeholder="Office, Home…" />
          </div>
          <div className="field-group">
            <label>Address</label>
            <input type="text" value={addresses[`${prefix}Address`]}
              onChange={e => handleAddressChange(`${prefix}Address`, e.target.value)}
              placeholder="Street address" />
          </div>
          <div className="field-group">
            <label>Suite / Unit</label>
            <input type="text" value={addresses[`${prefix}Suite`]}
              onChange={e => handleAddressChange(`${prefix}Suite`, e.target.value)}
              placeholder="Suite, apt, unit #" />
          </div>
        </div>
        <div className="form-grid three-col">
          <div className="field-group">
            <label>City</label>
            <input type="text" value={addresses[`${prefix}City`]}
              onChange={e => handleAddressChange(`${prefix}City`, e.target.value)}
              placeholder="City" />
          </div>
          <div className="field-group">
            <label>Country</label>
            <select value={addresses[countryKey]} onChange={e => {
              handleAddressChange(countryKey, e.target.value);
              handleAddressChange(stateKey, "");
              fetchRegions(e.target.value, regionSetter);
            }}>
              <option value="">Select Country</option>
              {countries.map(c => <option key={c.alpha3} value={c.alpha3}>{c.countryname}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label>State / Region</label>
            <select value={addresses[stateKey]}
              onChange={e => handleAddressChange(stateKey, e.target.value)}
              disabled={!addresses[countryKey] || !regionList.length}>
              <option value="">Select State / Region</option>
              {regionList.map((r, i) => <option key={i} value={r.regionname}>{r.regionname}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grid two-col">
          <div className="field-group">
            <label>Postal Code</label>
            <input type="text" value={addresses[`${prefix}PostalCode`]}
              onChange={e => handleAddressChange(`${prefix}PostalCode`, e.target.value)}
              placeholder="Postal code" />
          </div>
          <div className="field-group">
            <label>Delivery Notes</label>
            <input type="text" value={addresses[`${prefix}DeliveryNotes`]}
              onChange={e => handleAddressChange(`${prefix}DeliveryNotes`, e.target.value)}
              placeholder="Leave at door, ring bell…" />
          </div>
        </div>
      </>
    );

    return (
      <>
        <p className="form-section-label">Billing Address</p>
        {addrBlock("billing", billingRegions, setBillingRegions, "billingCountry", "billingState")}

        <hr className="form-divider" />
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
          <input type="checkbox" id="sameAsBilling" checked={addresses.sameAsBilling}
            onChange={e => handleAddressChange("sameAsBilling", e.target.checked)}
            style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "var(--highlight,#e9c548)" }} />
          <label htmlFor="sameAsBilling" style={{ cursor: "pointer", fontSize: "14px", color: "#ffffff", marginBottom: 0 }}>
            Shipping address same as billing
          </label>
        </div>

        {!addresses.sameAsBilling && (
          <>
            <p className="form-section-label">Shipping Address</p>
            {addrBlock("shipping", shippingRegions, setShippingRegions, "shippingCountry", "shippingState")}
          </>
        )}
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // STEP 3 — PAYMENT
  // ═══════════════════════════════════════════════════════════════
  const renderPayment = () => {
    const pi = (label, key, placeholder = "", hint = "") => (
      <div className="field-group" key={key}>
        <label>{label}</label>
        <input type="text" value={paymentInfo[key]}
          onChange={e => setPaymentInfo(p => ({ ...p, [key]: e.target.value }))}
          placeholder={placeholder} />
        {hint && <span className="field-hint">{hint}</span>}
      </div>
    );

    return (
      <>
        <p className="form-section-label">Bank Details</p>
        <div className="form-grid three-col">
          {pi("Bank Name",           "bankName",          "Bank name")}
          {pi("Bank Account Number", "bankAccountNumber", "Account number")}
          {pi("Bank Routing Number", "bankRoutingNumber", "Routing number")}
        </div>
        <div className="form-grid two-col">
          {pi("SWIFT Code (BIC)", "swiftCode", "BANKUS33XXX",           "8–11 alphanumeric characters")}
          {pi("IBAN",             "iban",      "GB29NWBK60161331926819","Up to 34 alphanumeric characters")}
        </div>

        <p className="form-section-label">Bank Address</p>
        <div className="form-grid three-col">
          {pi("Street Address", "bankAddress",    "Bank street address")}
          {pi("City",           "bankCity",       "City")}
          {pi("Postal Code",    "bankPostalCode", "Postal code")}
        </div>
        <div className="form-grid two-col">
          <div className="field-group">
            <label>Country</label>
            <select value={paymentInfo.bankCountry} onChange={e => {
              setPaymentInfo(p => ({ ...p, bankCountry: e.target.value, bankState: "" }));
              fetchRegions(e.target.value, setBankCountryRegions);
            }}>
              <option value="">Select Country</option>
              {countries.map(c => <option key={c.alpha3} value={c.alpha3}>{c.countryname}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label>State / Region</label>
            <select value={paymentInfo.bankState}
              onChange={e => setPaymentInfo(p => ({ ...p, bankState: e.target.value }))}
              disabled={!paymentInfo.bankCountry || !bankCountryRegions.length}>
              <option value="">Select State / Region</option>
              {bankCountryRegions.map((r, i) => <option key={i} value={r.regionname}>{r.regionname}</option>)}
            </select>
          </div>
        </div>

        <hr className="form-divider" />

        <GatewaySection title="Square" state={squareGateway} setState={setSquareGateway} fields={[
          { key: "account_id",    label: "Merchant ID",   placeholder: "Square Merchant ID"  },
          { key: "app_id",        label: "App ID",        placeholder: "sandbox-sq0idp-..."   },
          { key: "client_id",     label: "Client ID",     placeholder: "sandbox-sq0idp-..."   },
          { key: "client_secret", label: "Client Secret", placeholder: "sq0csp-..."           },
          { key: "bearer_token",  label: "Access Token",  placeholder: "EAAAEO..."            },
          { key: "refresh_token", label: "Refresh Token", placeholder: "EQAA..."              },
        ]} />

        <hr className="form-divider" />

        <GatewaySection title="PayPal" state={paypalGateway} setState={setPaypalGateway} fields={[
          { key: "account_id",    label: "Payer ID",      placeholder: "PayPal Payer ID"  },
          { key: "environment",   label: "Environment",   placeholder: "sandbox / live"   },
          { key: "client_id",     label: "Client ID",     placeholder: "AUp..."           },
          { key: "client_secret", label: "Client Secret", placeholder: "EL..."            },
        ]} />

        <hr className="form-divider" />

        <GatewaySection title="Braintree" state={braintreeGateway} setState={setBraintreeGateway} fields={[
          { key: "account_id",  label: "Merchant ID",  placeholder: "Braintree Merchant ID"  },
          { key: "environment", label: "Environment",  placeholder: "sandbox / production"   },
          { key: "client_id",   label: "Public Key",   placeholder: "Public key"             },
          { key: "api_key",     label: "Private Key",  placeholder: "Private key"            },
        ]} />

        <hr className="form-divider" />

        <GatewaySection title="Quickbooks" state={quickbooksGateway} setState={setQuickbooksGateway} fields={[
          { key: "account_id",    label: "Realm ID",      placeholder: "Quickbooks Realm ID" },
          { key: "client_id",     label: "Client ID",     placeholder: "AB..."               },
          { key: "client_secret", label: "Client Secret", placeholder: "xyz..."              },
          { key: "bearer_token",  label: "Access Token",  placeholder: "eyJl..."             },
          { key: "refresh_token", label: "Refresh Token", placeholder: "AB11..."             },
        ]} />
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  const docLabel    = session.docType === "PASSPORT" ? "passport" : "driver's license / ID";
  const isScanPhase = !extracted;
  const currentStep = STEPS[step];

  return (
    <div className={`step-card${extracted ? " wide" : ""}`}>
      <div className="step-icon">
        <span className="material-symbols-outlined">
          {isScanPhase ? "photo_camera" : "assignment_ind"}
        </span>
      </div>
      <h1>{isScanPhase ? `Scan your ${docLabel}` : currentStep.title}</h1>
      <p className="step-sub">
        {isScanPhase ? "Upload a clear photo of your ID to extract your details automatically." : currentStep.sub}
      </p>

      {error && <div className="error-banner">{error}</div>}

      {isScanPhase && renderScanPhase()}

      {extracted && (
        <>
          <StepIndicator step={step} />

          {step === 0 && renderPersonal()}
          {step === 1 && renderCompany()}
          {step === 2 && renderAddresses()}
          {step === 3 && renderPayment()}

          <div className="action-row" style={{ marginTop: "24px" }}>
            <button className="btn-ghost" onClick={handleBack}>← Back</button>
            {step < STEPS.length - 1 ? (
              <button className="btn-primary" onClick={handleNext}>Next →</button>
            ) : (
              <button className="btn-primary" onClick={handleSaveAndContinue}>
                Save &amp; Continue →
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
